import React, {useCallback, useState} from "react";
import {useAppSelector} from "../../../redux/hooks";
import {FontIcon} from "../../common/FontIcon";
import "./PitchDetectionIndicator.scss";
import {lessonPlayerStore} from "../../../redux/lesson/LessonPlayerStore";
import {settingsStore} from "../../../redux/settings/settingsStore";
import {useMicrophone} from "../../../hooks/microphone.hook";
import {displayErrorToUser} from "../../../services/error/error";
import PitchDetector from "../../../services/lesson/pitchDetector";
import * as Sentry from "@sentry/react";
import {logger} from "../../../services/log/logger";
import {usePitchDetectionStatus} from "../../../hooks/pitchDetectionStatus.hook";
import {MicrophoneAccessModal} from "../microphone/MicrophoneAccessModal";
import {SegmentModeEnum} from "../../../models/view/lessonPage/SegmentModeEnum";
import {GenericModal} from "../../common/genericModal/GenericModal";

interface IPitchDetectionIndicatorProps {
  canMicrophoneAccessModalBeShown: () => boolean;
}

export default function PitchDetectionIndicator({canMicrophoneAccessModalBeShown}: IPitchDetectionIndicatorProps) {
  const [isMicrophoneAccessModalOpen, setIsMicrophoneAccessModalOpen] = useState<boolean>(false);
  const {isPitchDetectionEnabled} = usePitchDetectionStatus();
  const isMidiDeviceConnected = useAppSelector(settingsStore.getIsMidiDeviceConnected);
  const microphone = useMicrophone();
  const [isMicrophoneAccessNeededModalOpen, setIsMicrophoneAccessNeededModalOpen] = useState<boolean>(false);

  const enablePitchDetectionIfNoKeyboardConnection = useCallback(() => {
    if (isMidiDeviceConnected) {
      displayErrorToUser("Scoring with the microphone cannot be enabled when a keyboard or a midi input is detected.");
      return;
    }

    if (!isMidiDeviceConnected && !isPitchDetectionEnabled) {
      // Modify Redux state before requesting microphone permissions so the user doesn't have to press the icon twice.
      lessonPlayerStore.setIsLoopEnabled(false);
      lessonPlayerStore.setIsDemoLeftHandSoundEnabled(false);
      lessonPlayerStore.setIsDemoRightHandSoundEnabled(false);
      lessonPlayerStore.setIsInstantFeedbackEnabled(true);

      if (microphone.permissionStatus == "requestable") {
        void PitchDetector.getMicrophoneAccess()
          .then((stream) => {
            // The first time the user is shown an OS-native dialog to grant microphone access/permissions,
            // this code does not run.
            // Instead, the page is reloaded.

            // Stop indicating that the microphone is in use.
            stream.getTracks().forEach((track) => track.stop());
          })
          // Ensure that the app doesn't crash.
          .catch((e) => {
            Sentry.captureException(e);
            logger.pitchDetection.error(e);
          });
      }
    }
  }, [microphone.permissionStatus, isMidiDeviceConnected, isPitchDetectionEnabled]);

  /**
   * This method is in this file because:
   * - It is currently only called when the pitch detection indicator is pressed.
   * - Its behavior changes based on its dependencies in Redux.
   *   - Microphone permissions
   *   - Whether a keyboard is connected
   *
   * If instead, the sole decider of placement was the method's responsibility, other places would be considered.
   * - pitchDetector.ts
   * - lessonService.ts
   * - playbackOrchestrator.ts
   * However, none of those are components or hooks,
   * so they can't simply respond to changes in the dependencies the way this component can.
   *
   * Its placement here avoids a lot of boilerplate that would be otherwise needed to subscribe to those dependencies.
   */
  const tryToEnablePitchDetection = useCallback(() => {
    switch (microphone.permissionStatus) {
      case "notset": // Fall through
      case "unrequestable":
        return;
      case "requestable":
        if (canMicrophoneAccessModalBeShown()) {
          setIsMicrophoneAccessModalOpen(true);
          break; // Don't handle the rest of the case statement.
        }
        enablePitchDetectionIfNoKeyboardConnection();
        break;
      case "granted":
        enablePitchDetectionIfNoKeyboardConnection();
        break;
      case "refused":
        setIsMicrophoneAccessNeededModalOpen(true);
        break;
    }
  }, [enablePitchDetectionIfNoKeyboardConnection, microphone.permissionStatus]);

  // Don't show the indicator on videos or follow alongs.
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  if (!segment || (segment.mode === SegmentModeEnum.play || segment.mode === SegmentModeEnum.followAlong)) {
    return null;
  }

  // Don't show the pitch detection indicator if a MIDI device is connected.
  if (isMidiDeviceConnected) {
    return null;
  }

  const onPitchDetectionIndicator_clicked = () => {
    if (!isPitchDetectionEnabled) {
      tryToEnablePitchDetection();
    } else {
      lessonPlayerStore.setIsInstantFeedbackEnabled(false);
    }
  }

  const icon = isPitchDetectionEnabled
    ? <FontIcon icon={"icon-microphone"}/>
    : <FontIcon icon={"icon-microphone-slash"}/>;

  const dynamicLabel = isPitchDetectionEnabled
    ? `Microphone is enabled`
    : `Microphone is disabled`;

  const dynamicClass = !isMidiDeviceConnected && !isPitchDetectionEnabled ? "clickable" : "";

  if (microphone.permissionStatus == "unrequestable" || microphone.permissionStatus == "notset") {
    return <></>;
  }

  return (
    <>
      <button role={"button"} title={dynamicLabel} aria-label={dynamicLabel} className={`pitch-detection-enabled-indicator-icon ${dynamicClass}`} onClick={onPitchDetectionIndicator_clicked}>
        {icon}
      </button>

      {/** Keep this modal outside the icon's div so clicks on its buttons don't trigger the icon's onClick handler. */}
      <MicrophoneAccessModal
        isOpen={isMicrophoneAccessModalOpen}
        close={() => {
          setIsMicrophoneAccessModalOpen(false)
        }}
        onRequestMicAccessButton_click={enablePitchDetectionIfNoKeyboardConnection}
      />

      <GenericModal
        onClose={() => setIsMicrophoneAccessNeededModalOpen(false)}
        canShow={isMicrophoneAccessNeededModalOpen}
        title={"Microphone access needed"}>
        Playground Sessions uses your microphone to listen and provide feedback. Please allow access in your device's system settings.
      </GenericModal>
    </>
  );
}
